import { defineStore } from "pinia";
import { ref } from "vue";

import { api, ApiError } from "@/api/client";
import type { ChatChannel, ChatMember, ChatMessage } from "@/api/types";

// Per-channel realtime chat: REST seed of recent messages + a `/ws/chat` stream
// that pushes each new message (text / trace / approval_*) live. Mirrors the
// machines store's connect/backoff pattern.
export const useChatStore = defineStore("chat", () => {
  const channels = ref<ChatChannel[]>([]);
  const activeId = ref<string | null>(null);
  const messages = ref<ChatMessage[]>([]);
  const error = ref<string | null>(null);
  const members = ref<ChatMember[]>([]); // worker participants + live presence
  const workers = ref<ChatMember[]>([]); // all org workers (for the add picker / mgmt)

  let socket: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelay = 1000;

  function describe(err: unknown): string {
    if (err instanceof ApiError) return `${err.code} (HTTP ${err.status})`;
    if (err instanceof Error) return err.message;
    return "unknown error";
  }

  async function loadChannels() {
    error.value = null;
    try {
      channels.value = (await api.chat.listChannels()).channels;
    } catch (err) {
      error.value = describe(err);
    }
  }

  async function createChannel(name: string, worker_preset?: string) {
    const ch = await api.chat.createChannel({ name, worker_preset });
    await loadChannels();
    return ch;
  }

  function upsert(m: ChatMessage) {
    const i = messages.value.findIndex((x) => x.id === m.id);
    if (i >= 0) messages.value[i] = m;
    else messages.value.push(m);
  }

  async function selectChannel(id: string) {
    closeSocket();
    activeId.value = id;
    messages.value = [];
    members.value = [];
    try {
      messages.value = (await api.chat.listMessages(id, 500)).messages;
      members.value = (await api.chat.listMembers(id)).members;
    } catch (err) {
      error.value = describe(err);
    }
    connectStream(id);
  }

  async function controlWorker(
    workerId: string,
    action: "stop" | "restart" | "pause" | "resume",
  ) {
    if (!activeId.value) return;
    try {
      await api.chat.workerControl(activeId.value, workerId, action);
    } catch (err) {
      error.value = describe(err);
    }
  }

  async function loadWorkers() {
    try {
      workers.value = (await api.chat.listWorkers()).workers;
    } catch (err) {
      error.value = describe(err);
    }
  }

  async function refreshMembers() {
    if (!activeId.value) return;
    try {
      members.value = (await api.chat.listMembers(activeId.value)).members;
    } catch (err) {
      error.value = describe(err);
    }
  }

  async function addMember(workerId: string) {
    if (!activeId.value) return;
    try {
      await api.chat.addMember(activeId.value, workerId);
      await refreshMembers();
    } catch (err) {
      error.value = describe(err);
    }
  }

  async function removeMember(workerId: string) {
    if (!activeId.value) return;
    try {
      await api.chat.removeMember(activeId.value, workerId);
      await refreshMembers();
    } catch (err) {
      error.value = describe(err);
    }
  }

  async function createWorker(name: string, preset?: string, zone?: string, memoryScope?: string) {
    try {
      await api.chat.createWorker({ name, preset, zone, memory_scope: memoryScope });
      await loadWorkers();
    } catch (err) {
      error.value = describe(err);
    }
  }

  async function deleteWorker(id: string) {
    try {
      await api.chat.deleteWorker(id);
      await loadWorkers();
    } catch (err) {
      error.value = describe(err);
    }
  }

  // Managed-runtime lifecycle: set the worker's desired state (the reconciler
  // starts/stops the process to match). Distinct from the per-channel stop/pause.
  async function setWorkerLifecycle(id: string, desired: "running" | "stopped") {
    try {
      await api.chat.workerLifecycle(id, desired);
      await loadWorkers();
    } catch (err) {
      error.value = describe(err);
    }
  }

  function connectStream(id: string) {
    if (socket) return;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${window.location.host}/ws/chat?channel_id=${id}`);
    socket = ws;
    ws.onmessage = (ev) => {
      try {
        const frame = JSON.parse(typeof ev.data === "string" ? ev.data : "");
        if (frame?.type === "message" && frame.message && activeId.value === id) {
          upsert(frame.message as ChatMessage);
        } else if (frame?.type === "worker_presence" && activeId.value === id) {
          const m = members.value.find((x) => x.id === frame.worker_id);
          if (m) m.status = String(frame.status);
        }
      } catch (e) {
        console.warn("chat frame parse failed", e);
      }
    };
    ws.onclose = () => {
      socket = null;
      if (activeId.value === id) scheduleReconnect(id);
    };
  }

  function scheduleReconnect(id: string) {
    if (reconnectTimer) return;
    reconnectTimer = setTimeout(
      () => {
        reconnectTimer = null;
        reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
        if (activeId.value === id) connectStream(id);
      },
      Math.min(reconnectDelay, 30_000),
    );
  }

  async function post(content: string) {
    if (!activeId.value) return;
    try {
      await api.chat.postMessage(activeId.value, content);
    } catch (err) {
      error.value = describe(err);
    }
  }

  async function approve(requestMessageId: string, decision: "approved" | "rejected") {
    if (!activeId.value) return;
    try {
      await api.chat.approve(activeId.value, requestMessageId, decision);
    } catch (err) {
      error.value = describe(err);
    }
  }

  function closeSocket() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (socket) {
      socket.close();
      socket = null;
    }
  }

  function stop() {
    activeId.value = null;
    closeSocket();
  }

  return {
    channels,
    activeId,
    messages,
    error,
    members,
    workers,
    loadChannels,
    createChannel,
    selectChannel,
    controlWorker,
    loadWorkers,
    addMember,
    removeMember,
    createWorker,
    deleteWorker,
    setWorkerLifecycle,
    post,
    approve,
    stop,
  };
});
